import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plug, ChevronRight, Settings, X, RefreshCw, AlertTriangle } from 'lucide-react';
import { getConnectedIntegrations, getAvailableIntegrations, integrations } from '../data/integrations';
import { toast } from 'sonner';

function SectionLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-0">
      {children}
    </div>
  );
}

function DashboardCard({ children, className = '' }) {
  return (
    <div className={`border border-zinc-800 bg-zinc-950/30 ${className}`}>
      {children}
    </div>
  );
}

export default function ConnectedIntegrationsWidget() {
  const { t } = useTranslation();
  const [connected, setConnected] = useState(() => getConnectedIntegrations());
  const [showDisconnectModal, setShowDisconnectModal] = useState(null);
  const available = getAvailableIntegrations();
  const totalAvailable = integrations.length;

  const handleDisconnect = (integration) => {
    setShowDisconnectModal(integration);
  };

  const confirmDisconnect = () => {
    if (showDisconnectModal) {
      // Update the integration status in data
      const idx = integrations.findIndex(i => i.id === showDisconnectModal.id);
      if (idx !== -1) {
        integrations[idx].status = 'available';
      }
      setConnected(getConnectedIntegrations());
      toast.success(`${showDisconnectModal.name} disconnected`);
      setShowDisconnectModal(null);
    }
  };

  return (
    <>
      <DashboardCard className="p-5 h-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-sm uppercase">{t('dashboard.connected')}</h3>
          <Link
            to="/marketplace/integrations"
            className="font-mono text-[10px] text-amber-500 hover:text-amber-400 uppercase tracking-wider transition-colors"
          >
            {t('dashboard.browse_all')}
          </Link>
        </div>

        {connected.length === 0 ? (
          <div className="text-center py-4">
            <Plug className="w-6 h-6 text-zinc-700 mx-auto mb-3" />
            <p className="font-display text-xs uppercase text-zinc-500 mb-2">
              {t('dashboard.no_integrations_connected')}
            </p>
            <p className="text-zinc-600 text-[10px] mb-3">
              {t('dashboard.no_integrations_desc')}
            </p>
            <Link
              to="/marketplace/integrations"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >
              {t('dashboard.browse_integrations')}
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-3">
              {connected.map((ig) => {
                const brandColor = ig.brandColor || '#f59e0b';
                return (
                  <div
                    key={ig.id}
                    className="flex items-center gap-3 p-2.5 border border-zinc-800 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors group"
                  >
                    <div
                      className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor: `${brandColor}15`,
                        border: `1px solid ${brandColor}25`,
                      }}
                    >
                      <span
                        className="text-[10px] font-display font-bold"
                        style={{ color: brandColor }}
                      >
                        {ig.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xs uppercase truncate">
                        {ig.name}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-emerald-500">
                          {t('dashboard.live')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link
                        to={`/marketplace/integrations/${ig.id}`}
                        className="p-1 text-zinc-500 hover:text-amber-500 transition-colors"
                        title={t('dashboard.manage')}
                      >
                        <Settings className="w-3 h-3" />
                      </Link>
                      <button
                        onClick={() => handleDisconnect(ig)}
                        className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                        title={t('dashboard.disconnect')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Available integrations */}
        {available.length > 0 && (
          <div className="pt-3 border-t border-zinc-800">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600 mb-2">
              {t('dashboard.available')}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {available.slice(0, 4).map((ig) => (
                <Link
                  key={ig.id}
                  to={`/marketplace/integrations/${ig.id}`}
                  className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 border border-zinc-800 hover:border-zinc-700 px-2 py-1 transition-colors"
                >
                  + {ig.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </DashboardCard>

      {/* Disconnect confirmation modal */}
      {showDisconnectModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-zinc-950 border border-zinc-800 p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="font-display text-sm uppercase text-white">
                  {t('dashboard.disconnect_title', { name: showDisconnectModal.name })}
                </h3>
              </div>
            </div>
            <p className="text-zinc-400 text-sm mb-6">
              {t('dashboard.disconnect_warning')}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDisconnectModal(null)}
                className="px-4 py-2 border border-zinc-700 text-zinc-300 hover:border-zinc-600 font-mono text-[10px] uppercase tracking-wider transition-colors"
              >
                {t('dashboard.keep_connected')}
              </button>
              <button
                onClick={confirmDisconnect}
                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 font-mono text-[10px] uppercase tracking-wider transition-colors"
              >
                {t('dashboard.disconnect_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
